import React from 'react';
import type { Empleado, Medicion, Sucursal } from '../types';
import { PageShell, inputStyle, primaryButton, ghostButton, dangerButton, luxEstado, Badge, EmptyState, T } from '../ui';
export function Mediciones({
  meds,
  sucs,
  emps,
  filtro,
  setFiltro,
  onNew,
  onEdit,
  onDelete,
  luxMin = 250
}: {
  meds: Medicion[];
  sucs: Sucursal[];
  emps: Empleado[];
  filtro: string;
  setFiltro: (v: string) => void;
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  luxMin?: number;
}) {
  const filtered = filtro ? meds.filter(m => m.sucursalId === filtro) : meds;
  return <PageShell title="Mediciones de luz" sub={`${filtered.length} registro${filtered.length !== 1 ? 's' : ''} · mínimo ${luxMin} lx`} action={<button onClick={onNew} style={primaryButton} data-id="element-658">
          + Nueva medición
        </button>} data-id="element-657">
      <style data-id="element-659">{`
        .med-card {
          background: ${T.surface};
          border: 1px solid ${T.border};
          border-radius: ${T.rMd}px;
          box-shadow: ${T.innerHi};
          padding: 16px 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          transition: border-color .2s, background .2s, transform .2s;
        }
        .med-card:hover { background: ${T.surfaceHover}; border-color: ${T.borderStrong}; transform: translateY(-2px); }
        .med-act { opacity: 0; transition: opacity .15s; }
        .med-card:hover .med-act { opacity: 1; }
        .med-list > * { animation: fadeUp .35s ease both; }
      `}</style>

      {/* Filter bar */}
      <div style={{
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      alignItems: 'center'
    }} data-id="element-660">
        <div style={{
        position: 'relative',
        flex: '0 0 auto'
      }} data-id="element-661">
          <select value={filtro} onChange={e => setFiltro(e.target.value)} style={{
          ...inputStyle,
          maxWidth: 220,
          paddingRight: 36,
          cursor: 'pointer'
        }} className="getr-input" data-id="element-662">
            <option value="" data-id="element-663">Todas las sucursales</option>
            {sucs.map(s => <option key={s.id} value={s.id} data-id="element-664">
                {s.nombre}
              </option>)}
          </select>
        </div>
        {filtro && <button onClick={() => setFiltro('')} className="getr-btn-ghost" style={ghostButton} data-id="element-665">
            Limpiar filtro ×
          </button>}
        <div style={{
        marginLeft: 'auto',
        fontSize: 12,
        color: T.textMuted,
        fontWeight: 600
      }} data-id="element-666">
          {filtered.length} de {meds.length}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? <EmptyState title="Sin mediciones registradas" hint="Registra una medición de luz para empezar a monitorear el rango." accent={T.brand} icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-668">
              <circle cx="12" cy="12" r="4" data-id="element-669" />
              <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" data-id="element-670" />
            </svg>} data-id="element-667" /> : <div className="med-list" style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 8
    }} data-id="element-671">
          {filtered.map(m => {
        const suc = sucs.find(s => s.id === m.sucursalId);
        const emp = emps.find(e => e.id === m.empleadoId);
        const est = luxEstado(m.lux, luxMin);
        return <div key={m.id} className="med-card" data-id="element-672">
                <div style={{
            display: 'flex',
            gap: 16,
            alignItems: 'center',
            flex: 1,
            minWidth: 0
          }} data-id="element-673">
                  {/* lux circle */}
                  <div style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              flexShrink: 0,
              background: est.soft,
              border: `1px solid ${est.color}40`,
              boxShadow: `0 0 18px ${est.color}22`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }} data-id="element-674">
                    <div style={{
                fontSize: 14,
                fontWeight: 800,
                color: est.color,
                lineHeight: 1
              }} data-id="element-675">
                      {m.lux}
                    </div>
                    <div style={{
                fontSize: 9,
                color: est.color,
                opacity: 0.7,
                fontWeight: 700
              }} data-id="element-676">
                      lx
                    </div>
                  </div>
                  <div style={{
              minWidth: 0
            }} data-id="element-677">
                    <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                marginBottom: 5
              }} data-id="element-678">
                      <span style={{
                  fontWeight: 700,
                  color: T.text,
                  fontSize: 14
                }} data-id="element-679">
                        {m.punto}
                      </span>
                      <Badge label={est.label} color={est.color} soft={est.soft} data-id="element-680" />
                    </div>
                    <div style={{
                fontSize: 12,
                color: T.textSecondary,
                fontWeight: 500
              }} data-id="element-681">
                      {suc?.nombre ?? '—'} · {emp?.nombre ?? 'Sin empleado'}
                    </div>
                    {m.notas && <div style={{
                fontSize: 11,
                color: T.textMuted,
                marginTop: 3,
                fontStyle: 'italic'
              }} data-id="element-682">
                        {m.notas}
                      </div>}
                  </div>
                </div>
                <div style={{
            fontSize: 11,
            color: T.textMuted,
            flexShrink: 0,
            textAlign: 'right',
            marginRight: 12
          }} data-id="element-683">
                  {new Date(m.fecha).toLocaleDateString('es-DO', {
              day: '2-digit',
              month: 'short'
            })}
                </div>
                <div className="med-act" style={{
            display: 'flex',
            gap: 6,
            flexShrink: 0
          }} data-id="element-684">
                  <button className="getr-btn-ghost" onClick={() => onEdit(m.id)} style={ghostButton} data-id="element-685">
                    Editar
                  </button>
                  <button onClick={() => onDelete(m.id)} style={dangerButton} data-id="element-686">
                    Eliminar
                  </button>
                </div>
              </div>;
      })}
        </div>}
    </PageShell>;
}